import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  ChevronDown, ChevronRight, FileSpreadsheet, Lock, Plus, Trash2,
  FileCheck2, FileWarning, FileClock, Loader2, Percent, BarChart3, ReceiptText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { financeApi } from "@/lib/unifiedApi";
import { storageService } from "@/lib/storage";
import {
  EN_MONTHS, fmt, INCOME_CATEGORIES, EXPENSE_CATEGORIES, NO_CATEGORY, CAT_COLORS,
  WHT_RATE_OPTIONS, parseCSV, extractTransactions, normalizeRows, taxParts, mergeTransactions,
  whtAmountFromRate, whtRateFromAmount, catBreakdown, buildMonths, computeTaxSummary, monthLabelEn, fmtDate,
  canViewFinance, isFinanceAdmin, isPayrollCategory,
} from "@/lib/financeUtils";
import { toast } from "sonner";

export default function FinancePage() {
  const { user } = useAuth();
  const isAdmin = isFinanceAdmin(user);
  const canView = canViewFinance(user);
  const restricted = canView && !isAdmin; // finance-staff (e.g. Plai): data entry only
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ fileName: "", importedAt: "" });
  const [docId, setDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // Mirror of rows for persistence from event handlers without stale closures
  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    let active = true;
    financeApi.load()
      .then((doc) => {
        if (!active || !doc) return;
        setDocId(doc.id);
        setRows(normalizeRows(doc.rows));
        setMeta({ fileName: doc.fileName || "", importedAt: doc.importedAt || "" });
      })
      .catch((e) => console.error("[finance] load", e))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [canView]);

  const [uploadingId, setUploadingId] = useState(null);

  // Mutate a transaction: compute next array from the ref (always current) so a
  // save that follows in the same tick reads the fresh value, not a stale render.
  const updateTxn = (id, patch) => {
    const next = rowsRef.current.map((t) => (t.id === id ? { ...t, ...patch } : t));
    rowsRef.current = next;
    setRows(next);
    return next;
  };
  const persistRows = async (rowsArr) => {
    try { await financeApi.save({ rows: rowsArr }); }
    catch (e) { console.error("[finance] save", e); toast.error("บันทึกไม่สำเร็จ"); }
  };
  // Text fields: edit on change, save on blur. Toggles/uploads: save immediately.
  const editTxn = (id, patch) => updateTxn(id, patch);
  const saveTxn = (id, patch) => persistRows(updateTxn(id, patch));
  const persistCurrent = () => persistRows(rowsRef.current);

  const uploadReceipt = async (t, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("ไฟล์ใหญ่เกิน 10MB"); return; }
    setUploadingId(t.id);
    try {
      const res = await storageService.uploadFile(file, "finance/current");
      saveTxn(t.id, { receiptUrl: res.url, receiptName: res.name });
      toast.success("อัพโหลดใบเสร็จแล้ว");
    } catch (e) {
      console.error("[finance] upload", e);
      toast.error("อัพโหลดไม่สำเร็จ");
    } finally {
      setUploadingId(null);
    }
  };

  // ── Aggregations (shared helpers) ──
  // Finance-staff never see payroll rows (kept in `rows` for saves, hidden from view).
  const displayRows = useMemo(() => (restricted ? rows.filter((t) => !isPayrollCategory(t.category)) : rows), [rows, restricted]);
  const months = useMemo(() => buildMonths(displayRows), [displayRows]);

  const totals = useMemo(() => {
    const income = months.reduce((s, m) => s + m.income, 0);
    const expense = months.reduce((s, m) => s + m.expense, 0);
    return { income, expense, net: income - expense };
  }, [months]);

  const maxAbsNet = useMemo(() => Math.max(1, ...months.map((m) => Math.abs(m.net))), [months]);
  // Finance-staff see the category breakdown too — but without payroll rows.
  const categorySummary = useMemo(() => catBreakdown(displayRows), [displayRows]);
  const taxSummary = useMemo(() => computeTaxSummary(rows), [rows]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { toast.error("รองรับไฟล์ .csv เท่านั้น"); return; }
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      const incoming = extractTransactions(parsed);
      const existing = rowsRef.current;
      // Row-level merge: skip lines already imported (keeping their edits),
      // append only new ones — works for mid-month top-ups of the same month.
      const { merged, added, skipped, addedMonths } = mergeTransactions(existing, incoming);

      if (existing.length > 0 && added.length === 0) {
        toast(`ไม่มีรายการใหม่ — ข้าม ${skipped} รายการที่มีอยู่แล้ว`);
        return;
      }

      const importedAt = new Date().toISOString();
      await financeApi.save({ fileName: file.name, importedAt, rows: merged });
      setDocId("current");
      setRows(merged);
      setMeta({ fileName: file.name, importedAt });
      if (existing.length === 0) {
        toast.success(`นำเข้า ${added.length} รายการ`);
      } else {
        toast.success(`เพิ่ม ${added.length} รายการใหม่ (${addedMonths.size} เดือน)${skipped ? ` · ข้าม ${skipped} รายการเดิม` : ""}`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || "อ่านไฟล์ไม่สำเร็จ");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const clearData = async () => {
    if (!window.confirm("ล้างข้อมูลการเงินทั้งหมด?")) return;
    setBusy(true);
    try {
      await financeApi.clear();
      setRows([]); setDocId(null); setMeta({ fileName: "", importedAt: "" });
      toast.success("ล้างข้อมูลแล้ว");
    } catch { toast.error("ล้างข้อมูลไม่สำเร็จ"); }
    finally { setBusy(false); }
  };

  // ── Admin guard ──
  if (!canView) {
    return (
      <div className="page-container">
        <div className="rounded-2xl bg-white border border-gray-200 py-20 flex flex-col items-center gap-3 mt-8">
          <Lock size={40} className="text-gray-300" />
          <p className="text-gray-500 font-medium">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="finance-page">
      <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      <datalist id="fin-cat-income">{INCOME_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="fin-cat-expense">{EXPENSE_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="fin-wht-rates">{WHT_RATE_OPTIONS.map((r) => <option key={r} value={r} />)}</datalist>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Wallet size={28} className="text-[#3b6ef5]" /> Finance
          </h1>
          <p className="text-gray-500 mt-1">{restricted ? "กรอกรายละเอียดแต่ละรายการ" : "รายรับรายจ่าย & กำไรขาดทุนรายเดือน (นำเข้าจากไฟล์ statement)"}</p>
        </div>
        {!restricted && rows.length > 0 && (
          <div className="flex items-center gap-2 self-start">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
              <Plus size={15} className="mr-1.5" /> เพิ่มไฟล์ (เดือนใหม่)
            </Button>
            <Button variant="outline" onClick={clearData} disabled={busy} className="text-red-500 hover:text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 size={15} />
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-[#3b6ef5] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        restricted ? (
          <div className="rounded-2xl bg-white border border-gray-200 py-16 text-center text-gray-400">ยังไม่มีข้อมูลการเงิน</div>
        ) : (
        /* Empty / import dropzone */
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed py-20 flex flex-col items-center gap-4 cursor-pointer transition-colors ${dragOver ? "border-[#3b6ef5] bg-blue-50/50" : "border-gray-300 bg-white hover:border-gray-400"}`}
        >
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
            <FileSpreadsheet size={30} className="text-[#3b6ef5]" />
          </div>
          <div className="text-center">
            <p className="text-gray-700 font-semibold">{busy ? "กำลังอ่านไฟล์..." : "ลากไฟล์ statement (.csv) มาวาง หรือคลิกเพื่อเลือก"}</p>
            <p className="text-gray-400 text-sm mt-1">ระบบจะแบ่งเดือนอัตโนมัติจากคอลัมน์ “วันที่” และสรุปรายรับ − รายจ่าย</p>
          </div>
        </div>
        )
      ) : (
        <>
          {/* Summary cards — income & expense shown to finance-staff; net (P&L) hidden */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="rounded-2xl bg-white border border-gray-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0"><ArrowUpRight size={18} className="text-emerald-600" /></div>
              <div><p className="text-[11px] text-gray-400">รายรับรวม</p><p className="text-xl font-extrabold text-emerald-600">฿{fmt(totals.income)}</p></div>
            </div>
            <div className="rounded-2xl bg-white border border-gray-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0"><ArrowDownRight size={18} className="text-red-600" /></div>
              <div><p className="text-[11px] text-gray-400">รายจ่ายรวม</p><p className="text-xl font-extrabold text-red-600">฿{fmt(totals.expense)}</p></div>
            </div>
            {!restricted && (
            <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ background: totals.net >= 0 ? "#f0fdf4" : "#fef2f2", borderColor: totals.net >= 0 ? "#bbf7d0" : "#fecaca" }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: totals.net >= 0 ? "#dcfce7" : "#fee2e2" }}>
                {totals.net >= 0 ? <TrendingUp size={18} className="text-emerald-600" /> : <TrendingDown size={18} className="text-red-600" />}
              </div>
              <div>
                <p className="text-[11px]" style={{ color: totals.net >= 0 ? "#16a34a" : "#dc2626" }}>{totals.net >= 0 ? "กำไรสุทธิ" : "ขาดทุนสุทธิ"}</p>
                <p className="text-xl font-extrabold" style={{ color: totals.net >= 0 ? "#16a34a" : "#dc2626" }}>฿{fmt(totals.net)}</p>
              </div>
            </div>
            )}
          </div>

          {/* Net-per-month bar chart — hidden from finance-staff (P&L) */}
          {!restricted && (
          <div className="rounded-2xl bg-white border border-gray-200 p-4 mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-4">กำไร/ขาดทุน รายเดือน</p>
            <div className="flex items-end justify-around gap-2" style={{ height: 140 }}>
              {months.map((m) => {
                const h = (Math.abs(m.net) / maxAbsNet) * 52;
                const pos = m.net >= 0;
                return (
                  <div key={m.key} className="flex-1 flex flex-col items-center justify-center" style={{ minWidth: 0 }}>
                    <div className="flex flex-col items-center justify-end" style={{ height: 56 }}>
                      {pos && <div className="rounded-t" style={{ width: 22, height: Math.max(2, h), background: "#22c55e" }} title={`+${fmt(m.net)}`} />}
                    </div>
                    <div className="w-full" style={{ height: 1, background: "#e5e7eb" }} />
                    <div className="flex flex-col items-center justify-start" style={{ height: 56 }}>
                      {!pos && <div className="rounded-b" style={{ width: 22, height: Math.max(2, h), background: "#ef4444" }} title={`-${fmt(Math.abs(m.net))}`} />}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1 truncate w-full text-center">{EN_MONTHS[Number(m.key.split("-")[1]) - 1]}</p>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Category breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {[
              { title: "รายรับตามหมวดหมู่", data: categorySummary.income, total: totals.income, accent: "#10b981" },
              { title: "รายจ่ายตามหมวดหมู่", data: categorySummary.expense, total: totals.expense, accent: "#ef4444" },
            ].map(({ title, data, total, accent }) => {
              const max = data[0]?.value || 1;
              return (
                <div key={title} className="rounded-2xl bg-white border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-700">{title}</p>
                    <p className="text-sm font-bold" style={{ color: accent }}>฿{fmt(total)}</p>
                  </div>
                  {data.length === 0 ? (
                    <p className="text-xs text-gray-400 py-6 text-center">ยังไม่มีข้อมูล</p>
                  ) : (
                    <div className="space-y-2.5">
                      {data.map((c, i) => (
                        <div key={c.name}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="flex items-center gap-1.5 text-gray-600 min-w-0">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.name === NO_CATEGORY ? "#cbd5e1" : CAT_COLORS[i % CAT_COLORS.length] }} />
                              <span className="truncate">{c.name}</span>
                            </span>
                            <span className="text-gray-500 flex-shrink-0 ml-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                              ฿{fmt(c.value)} <span className="text-gray-400">· {total > 0 ? Math.round((c.value / total) * 100) : 0}%</span>
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(c.value / max) * 100}%`, background: c.name === NO_CATEGORY ? "#cbd5e1" : CAT_COLORS[i % CAT_COLORS.length] }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tax summary — VAT & withholding */}
          {taxSummary.tagged > 0 && (
            <div className="rounded-2xl bg-white border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Percent size={15} className="text-[#3b6ef5]" /> สรุปภาษี</p>
                <span className="flex items-center gap-3">
                  {taxSummary.invoicePending > 0 && (
                    <span className="text-[11px] font-medium text-[#2563eb] flex items-center gap-1">
                      <ReceiptText size={13} /> {taxSummary.invoicePending} รายการยังไม่ส่งใบกำกับภาษี
                    </span>
                  )}
                  {taxSummary.missing > 0 && (
                    <span className="text-[11px] font-medium text-amber-600 flex items-center gap-1">
                      <FileWarning size={13} /> {taxSummary.missing} รายการยังไม่อัพใบเสร็จ
                    </span>
                  )}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                  <p className="text-[11px] text-gray-500">VAT ซื้อ (ขอคืน)</p>
                  <p className="text-base font-bold text-gray-900">฿{fmt(taxSummary.vatInput)}</p>
                </div>
                <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                  <p className="text-[11px] text-gray-500">VAT ขาย (นำส่ง)</p>
                  <p className="text-base font-bold text-gray-900">฿{fmt(taxSummary.vatOutput)}</p>
                </div>
                <div className="rounded-xl px-3 py-2.5" style={{ background: taxSummary.vatNet >= 0 ? "#fef2f2" : "#f0fdf4" }}>
                  <p className="text-[11px]" style={{ color: taxSummary.vatNet >= 0 ? "#dc2626" : "#16a34a" }}>VAT สุทธิ {taxSummary.vatNet >= 0 ? "(ต้องจ่าย)" : "(ขอคืน)"}</p>
                  <p className="text-base font-bold" style={{ color: taxSummary.vatNet >= 0 ? "#dc2626" : "#16a34a" }}>฿{fmt(Math.abs(taxSummary.vatNet))}</p>
                </div>
                <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                  <p className="text-[11px] text-gray-500">WHT เราหักไว้ (นำส่ง)</p>
                  <p className="text-base font-bold text-gray-900">฿{fmt(taxSummary.whtWithheld)}</p>
                </div>
                <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                  <p className="text-[11px] text-gray-500">WHT ถูกหัก (เครดิต)</p>
                  <p className="text-base font-bold text-gray-900">฿{fmt(taxSummary.whtDeducted)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Monthly P&L ledger table */}
          <div className="rounded-2xl bg-white border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ borderCollapse: "collapse", minWidth: restricted ? 600 : 820 }}>
                <thead>
                  <tr className="bg-gray-100 text-gray-500 text-[11px] uppercase">
                    <th className="text-left font-semibold px-4 py-2.5">เดือน</th>
                    <th className="text-right font-semibold px-4 py-2.5">รายรับ</th>
                    <th className="text-right font-semibold px-4 py-2.5">รายจ่าย</th>
                    {!restricted && (<>
                      <th className="text-right font-semibold px-4 py-2.5">กำไร/ขาดทุน</th>
                      <th className="text-right font-semibold px-4 py-2.5">ยอดสะสม</th>
                      <th className="text-right font-semibold px-4 py-2.5">ยอดคงเหลือ</th>
                    </>)}
                    <th className="text-right font-semibold px-4 py-2.5">รายการ</th>
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                  {months.map((m, idx) => {
                    const open = expanded === m.key;
                    return (
                      <Fragment key={m.key}>
                        <tr onClick={() => setExpanded(open ? null : m.key)}
                          className={`border-t border-gray-100 cursor-pointer hover:bg-blue-50/40 ${idx % 2 ? "bg-gray-50/60" : "bg-white"}`}>
                          <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5">
                              {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                              {monthLabelEn(m.key)}
                              {!restricted && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/finance/month/${m.key}`); }}
                                  title="ดู Dashboard เดือนนี้ (เต็มหน้า)"
                                  className="ml-1 p-1 rounded-md text-gray-400 hover:text-[#3b6ef5] hover:bg-blue-50"
                                >
                                  <BarChart3 size={14} />
                                </button>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-emerald-600">{fmt(m.income)}</td>
                          <td className="px-4 py-2.5 text-right text-red-600">{fmt(m.expense)}</td>
                          {!restricted && (<>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: m.net >= 0 ? "#16a34a" : "#dc2626" }}>
                            {m.net >= 0 ? "+" : "−"}{fmt(Math.abs(m.net))}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold" style={{ color: m.cumulative >= 0 ? "#0f766e" : "#dc2626" }}>
                            {fmt(m.cumulative)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-500">{m.closingBalance != null ? fmt(m.closingBalance) : "—"}</td>
                          </>)}
                          <td className="px-4 py-2.5 text-right text-gray-400">
                            <span className="inline-flex items-center gap-1.5 justify-end">
                              {m.missingReceipts > 0 && (
                                <span title={`${m.missingReceipts} รายการมีภาษีแต่ยังไม่อัพใบเสร็จ`} className="inline-flex items-center gap-0.5 text-amber-600 font-medium">
                                  <FileWarning size={12} />{m.missingReceipts}
                                </span>
                              )}
                              {m.count}
                            </span>
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={restricted ? 4 : 7} className="px-0 py-0 bg-blue-50/20 border-t border-gray-100">
                              <div className="max-h-[30rem] overflow-y-auto divide-y divide-gray-100">
                                {(restricted ? m.txns.filter((t) => !isPayrollCategory(t.category)) : m.txns).map((t) => {
                                  const isIncome = Number(t.income) > 0;
                                  const p = taxParts(t);
                                  const showTax = t.hasVat || t.wht;
                                  return (
                                    <div key={t.id} className="px-4 py-2.5">
                                      {/* Line 1 — date · statement desc · detail · amount */}
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-400 w-[76px] flex-shrink-0">{fmtDate(t.date)}</span>
                                        <span className="min-w-0 truncate text-gray-500 flex-1" title={t.desc}>{t.desc || "-"}</span>
                                        <input
                                          value={t.label || ""}
                                          onChange={(e) => editTxn(t.id, { label: e.target.value })}
                                          onBlur={persistCurrent}
                                          placeholder={isIncome ? "รายละเอียดรายรับ..." : "รายละเอียดรายจ่าย..."}
                                          className="w-52 px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b6ef5] focus:border-[#3b6ef5]"
                                        />
                                        <span className={`w-28 text-right font-semibold flex-shrink-0 ${isIncome ? "text-emerald-600" : "text-red-600"}`} style={{ fontVariantNumeric: "tabular-nums" }}>
                                          {isIncome ? "+" : "−"}{fmt(isIncome ? t.income : t.expense)}
                                        </span>
                                      </div>
                                      {/* Line 2 — category · VAT · WHT · derived · receipt */}
                                      <div className="flex items-center gap-2 flex-wrap mt-1.5 pl-[84px]">
                                        <input
                                          value={t.category || ""}
                                          list={isIncome ? "fin-cat-income" : "fin-cat-expense"}
                                          onChange={(e) => editTxn(t.id, { category: e.target.value })}
                                          onBlur={persistCurrent}
                                          placeholder="หมวดหมู่..."
                                          className="w-36 px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-600 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#3b6ef5] focus:border-[#3b6ef5]"
                                        />
                                        <label className="flex items-center gap-1 text-[11px] text-gray-600 cursor-pointer">
                                          <input type="checkbox" checked={!!t.hasVat} onChange={(e) => saveTxn(t.id, { hasVat: e.target.checked, vatTouched: true })} className="w-3.5 h-3.5 accent-[#3b6ef5]" />
                                          มี VAT
                                        </label>
                                        {isIncome && (
                                          <label
                                            title={t.taxInvoiceSent ? "ส่งใบกำกับภาษีให้ลูกค้าแล้ว" : "ยังไม่ได้ส่งใบกำกับภาษีให้ลูกค้า"}
                                            className={`flex items-center gap-1 text-[11px] cursor-pointer ${t.taxInvoiceSent ? "text-[#2563eb] font-medium" : "text-blue-300 hover:text-blue-500"}`}
                                          >
                                            <input type="checkbox" checked={!!t.taxInvoiceSent} onChange={(e) => saveTxn(t.id, { taxInvoiceSent: e.target.checked })} className="hidden" />
                                            <ReceiptText size={14} />
                                            ส่งใบกำกับแล้ว
                                          </label>
                                        )}
                                        <label className="flex items-center gap-1 text-[11px] text-gray-600 cursor-pointer">
                                          <input type="checkbox" checked={!!t.wht} onChange={(e) => saveTxn(t.id, { wht: e.target.checked })} className="w-3.5 h-3.5 accent-[#3b6ef5]" />
                                          {isIncome ? "ถูกหัก ณ ที่จ่าย" : "หัก ณ ที่จ่าย"}
                                        </label>
                                        {t.wht && (
                                          <>
                                            <span className="inline-flex items-center gap-0.5">
                                              <input
                                                type="number" min="0" max="100" step="0.5" list="fin-wht-rates"
                                                value={t.whtRate ?? ""}
                                                onChange={(e) => { const r = e.target.value === "" ? 0 : Number(e.target.value); editTxn(t.id, { whtRate: r, whtAmount: whtAmountFromRate(t, r) }); }}
                                                onBlur={persistCurrent}
                                                placeholder="%"
                                                className="w-12 px-1.5 py-1 rounded-md border border-gray-200 bg-white text-gray-600 text-[11px] text-right focus:outline-none focus:ring-1 focus:ring-[#3b6ef5]"
                                              />
                                              <span className="text-[10px] text-gray-400">%</span>
                                            </span>
                                            <span className="inline-flex items-center gap-0.5">
                                              <span className="text-[10px] text-gray-400">฿</span>
                                              <input
                                                type="number" min="0" step="0.01"
                                                value={t.whtAmount ?? ""}
                                                onChange={(e) => { const a = e.target.value === "" ? 0 : Number(e.target.value); editTxn(t.id, { whtAmount: a, whtRate: whtRateFromAmount(t, a) }); }}
                                                onBlur={persistCurrent}
                                                placeholder="ยอดหัก"
                                                className="w-20 px-1.5 py-1 rounded-md border border-gray-200 bg-white text-gray-600 text-[11px] text-right focus:outline-none focus:ring-1 focus:ring-[#3b6ef5]"
                                              />
                                            </span>
                                          </>
                                        )}
                                        {showTax && (
                                          <span className="text-[10px] text-gray-400" style={{ fontVariantNumeric: "tabular-nums" }}>
                                            ฐาน ฿{fmt(p.base)}{t.hasVat ? ` · VAT ฿${fmt(p.vat)}` : ""}{t.wht ? ` · หัก ฿${fmt(p.wht)}` : ""}
                                          </span>
                                        )}
                                        <div className="ml-auto flex items-center gap-2">
                                          {uploadingId === t.id ? (
                                            <span className="text-[11px] text-gray-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> อัพโหลด...</span>
                                          ) : t.receiptUrl ? (
                                            <>
                                              <a href={t.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-600 hover:underline flex items-center gap-1"><FileCheck2 size={13} /> ดูใบเสร็จ</a>
                                              <label className="text-[11px] text-gray-400 hover:text-gray-600 cursor-pointer">
                                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadReceipt(t, e.target.files?.[0])} />
                                                เปลี่ยน
                                              </label>
                                            </>
                                          ) : (
                                            <label title="รอเอกสาร — คลิกเพื่ออัพโหลดใบเสร็จ/ใบกำกับภาษี" className="text-amber-500 hover:text-amber-600 cursor-pointer inline-flex items-center">
                                              <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadReceipt(t, e.target.files?.[0])} />
                                              <FileClock size={15} />
                                            </label>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-200 font-bold text-gray-900" style={{ fontVariantNumeric: "tabular-nums" }}>
                    <td className="px-4 py-3">รวมทั้งหมด</td>
                    <td className="px-4 py-3 text-right text-emerald-600">{fmt(totals.income)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{fmt(totals.expense)}</td>
                    {!restricted && (<>
                    <td className="px-4 py-3 text-right" style={{ color: totals.net >= 0 ? "#16a34a" : "#dc2626" }}>
                      {totals.net >= 0 ? "+" : "−"}{fmt(Math.abs(totals.net))}
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3" />
                    </>)}
                    <td className="px-4 py-3 text-right text-gray-400">{restricted ? displayRows.length : rows.length}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {meta.fileName && (
            <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
              <FileSpreadsheet size={12} /> {meta.fileName}
              {meta.importedAt && ` · นำเข้าเมื่อ ${new Date(meta.importedAt).toLocaleString("th-TH")}`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
